import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserMinus, UserPlus } from 'lucide-react';
import { get, del } from '@/api/client';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Button } from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { EmptyState } from '@/components/atoms/EmptyState';
import { useAuthStore } from '@/store/authStore';
import { confirmModal } from '@/store/confirmStore';

interface FollowUser {
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
  bio: string | null;
}

export function UserFollowingPage() {
  const { userId } = useParams<{ userId: string }>();
  const me = useAuthStore((s) => s.me);
  const isMyList = me?.userId === userId;
  const queryClient = useQueryClient();

  const unfollowMutation = useMutation({
    mutationFn: (targetId: string) => del(`/follow/${targetId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'following'] });
      queryClient.invalidateQueries({ queryKey: ['users', userId] });
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['users', userId, 'following'],
    queryFn: () => get<{ list: FollowUser[] }>(`/users/${userId}/following`),
    enabled: !!userId,
  });

  const count = data?.list?.length ?? 0;

  return (
    <div className="container-base py-10">
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 sm:p-8">
        <header className="mb-6">
          <h1 className="text-3xl font-extrabold">팔로잉</h1>
          <p className="mt-2 text-sm text-[var(--color-text-sub)]">
            {count > 0
              ? `${count.toLocaleString('ko-KR')}명의 유저를 팔로우 중이에요. 팔로우한 유저의 새 글을 피드에서 확인할 수 있어요.`
              : '아직 팔로우 중인 유저가 없어요. 관심있는 유저를 팔로우하면 새 글이 피드에 표시돼요.'}
          </p>
        </header>

        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        )}

        {data?.list && data.list.length > 0 ? (
          <ul className="space-y-2">
            {data.list.map((u) => (
              <li
                key={u.user_id}
                className="flex items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 transition-colors hover:border-[var(--color-primary)]/40"
              >
                <Link
                  to={`/users/${u.user_id}`}
                  className="flex min-w-0 flex-1 items-center gap-3"
                >
                  <Avatar src={u.profile_image_url ?? undefined} alt={u.nickname} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-semibold">{u.nickname}</span>
                      <LevelBadge level={u.level} size="sm" />
                    </div>
                    {u.bio && (
                      <p className="mt-0.5 line-clamp-1 text-sm text-[var(--color-text-sub)]">
                        {u.bio}
                      </p>
                    )}
                  </div>
                </Link>
                {isMyList && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      const ok = await confirmModal({
                        title: '팔로우 취소',
                        message: `${u.nickname} 팔로우를 취소할까요?`,
                        confirmLabel: '취소하기',
                        cancelLabel: '유지',
                      });
                      if (ok) unfollowMutation.mutate(u.user_id);
                    }}
                    loading={unfollowMutation.isPending}
                  >
                    <UserMinus className="h-4 w-4" />
                    언팔로우
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          !isLoading && (
            <EmptyState
              icon={UserPlus}
              title="팔로잉하는 유저가 없어요"
              description="관심 있는 유저를 팔로우하면 새 글이 피드에 자동으로 표시돼요."
            />
          )
        )}
      </div>
    </div>
  );
}
